import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  AD_SOYAD: "",
  UZMANLIK_ALANI: "",
  BIO: "",
  PROFIL_FOTO: "",
  INSTAGRAM: "",
  YOUTUBE: "",
  PUAN: 0,
  YORUM_SAYISI: 0,
  AKTIF: true,
};

const Uzmanlar = () => {
  document.title = "Uzmanlar | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/UZMANLAR?$orderby=ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Uzmanlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      AD_SOYAD: row.AD_SOYAD || "",
      UZMANLIK_ALANI: row.UZMANLIK_ALANI || "",
      BIO: row.BIO || "",
      PROFIL_FOTO: row.PROFIL_FOTO || "",
      INSTAGRAM: row.INSTAGRAM || "",
      YOUTUBE: row.YOUTUBE || "",
      PUAN: row.PUAN ?? 0,
      YORUM_SAYISI: row.YORUM_SAYISI ?? 0,
      AKTIF: row.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD_SOYAD.trim()) {
      setError("Ad soyad zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        AD_SOYAD: formData.AD_SOYAD.trim(),
        UZMANLIK_ALANI: formData.UZMANLIK_ALANI || null,
        BIO: formData.BIO || null,
        PROFIL_FOTO: formData.PROFIL_FOTO || null,
        INSTAGRAM: formData.INSTAGRAM || null,
        YOUTUBE: formData.YOUTUBE || null,
        PUAN: Number(formData.PUAN) || 0,
        YORUM_SAYISI: Number(formData.YORUM_SAYISI) || 0,
        AKTIF: formData.AKTIF,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/UZMANLAR(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Uzman güncellendi.");
      } else {
        await fetch(`${BASE_URL}/UZMANLAR`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Uzman eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu uzmanı silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/UZMANLAR(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Uzman silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Uzman",
      accessorKey: "AD_SOYAD",
      enableSorting: true,
      cell: (c) => {
        const row = c.row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            {row.PROFIL_FOTO
              ? <img src={row.PROFIL_FOTO} alt="" style={{ width: 38, height: 38, objectFit: "cover", borderRadius: "50%" }} />
              : <span className="avatar-title rounded-circle bg-light text-primary" style={{ width: 38, height: 38, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><i className="bx bx-user" /></span>}
            <span className="fw-medium">{c.getValue() || "---"}</span>
          </div>
        );
      },
    },
    {
      header: "Uzmanlık Alanı",
      accessorKey: "UZMANLIK_ALANI",
      enableSorting: true,
      cell: (c) => c.getValue() ? <Badge color="info" pill>{c.getValue()}</Badge> : <span className="text-muted">---</span>,
    },
    {
      header: "Puan",
      accessorKey: "PUAN",
      enableSorting: true,
      cell: (c) => (
        <span><i className="bx bxs-star text-warning" /> {Number(c.getValue() || 0).toFixed(1)} <small className="text-muted">({c.row.original.YORUM_SAYISI ?? 0})</small></span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      cell: (c) => <Badge color={c.getValue() ? "success" : "danger"}>{c.getValue() ? "Aktif" : "Pasif"}</Badge>,
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Kullanıcılar" breadcrumbItem="Uzmanlar" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Değerleme Uzmanları</h4>
                      <p className="text-muted small mb-0">
                        Mobil uygulamada gösterilen uzmanları buradan tanımlayın ve yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Uzman
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Uzman ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-account-edit-outline" : "mdi-account-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `Uzman Düzenle (#${formData.ID})` : "Yeni Uzman Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Ad Soyad <span className="text-danger">*</span></Label>
                <Input value={formData.AD_SOYAD} onChange={(e) => f("AD_SOYAD", e.target.value)} placeholder="Uzman adı" maxLength={150} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Uzmanlık Alanı</Label>
                <Input value={formData.UZMANLIK_ALANI} onChange={(e) => f("UZMANLIK_ALANI", e.target.value)} placeholder="Örn: Büyükbaş Hayvancılık" maxLength={150} />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Biyografi</Label>
                <Input type="textarea" rows={4} value={formData.BIO} onChange={(e) => f("BIO", e.target.value)} placeholder="Kısa biyografi" />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Profil Foto URL</Label>
                <Input value={formData.PROFIL_FOTO} onChange={(e) => f("PROFIL_FOTO", e.target.value)} placeholder="https://..." />
                {formData.PROFIL_FOTO && <img src={formData.PROFIL_FOTO} alt="önizleme" className="mt-2" style={{ height: 60, borderRadius: "50%" }} />}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Instagram</Label>
                <Input value={formData.INSTAGRAM} onChange={(e) => f("INSTAGRAM", e.target.value)} placeholder="@kullanici veya URL" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">YouTube</Label>
                <Input value={formData.YOUTUBE} onChange={(e) => f("YOUTUBE", e.target.value)} placeholder="Kanal URL" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Puan</Label>
                <Input type="number" step="0.1" min="0" max="5" value={formData.PUAN} onChange={(e) => f("PUAN", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Yorum Sayısı</Label>
                <Input type="number" min="0" value={formData.YORUM_SAYISI} onChange={(e) => f("YORUM_SAYISI", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup check className="mt-4">
                <Input type="checkbox" id="uzmanAktif" checked={formData.AKTIF} onChange={(e) => f("AKTIF", e.target.checked)} />
                <Label check for="uzmanAktif" className="fw-bold ms-1">Aktif</Label>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Uzmanlar;
